import { registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import { isCapacitorApp } from '@/shared/libs/platform/is-capacitor-app';

type PaymateMerchantInfo = {
  merchantId: string;
  payPilotProfileId: string;
  partnerId: string;
  profileId: string;
  brandId: string;
  terminalId: string;
  jwt: string;
  accessToken: string;
  amount: number;
  userId: string;
  applicationId: number;
};

type PaymateMerchantReferenceField = {
  fieldName: string;
  fieldValue: string;
};

export type PaymateInitPayload = {
  merchantInfo: PaymateMerchantInfo;
  apiUrl: string;
  options?: {
    enableLogging?: boolean;
    timeoutMs?: number;
    retryAttempts?: number;
    cleanupQrOnReturn?: boolean;
    sdkPackage?: string;
  };
};

export type PaymateStartPayload = {
  app?: string;
  merchantReferenceData: Record<string, PaymateMerchantReferenceField>;
};

export type PaymateStatusEvent = {
  status: 'initialized' | 'started' | 'success' | 'error' | 'warning' | 'state';
  app?: string;
  automation?: boolean;
  state?: string;
  message?: string;
};

export type PaymateErrorEvent = {
  code: string;
  message: string;
};

export type PaymateReturnEvent = {
  source: 'resume' | 'deeplink';
  url?: string;
};

type PaymatePlugin = {
  initialize(payload: PaymateInitPayload): Promise<{ initialized: boolean }>;
  startHandoff(payload: PaymateStartPayload): Promise<{ started: boolean; app: string; handoff: boolean }>;
  startMayaHandoff(payload: Pick<PaymateStartPayload, 'merchantReferenceData'>): Promise<{ started: boolean; app: string; handoff: boolean }>;
  removeQrImages(): Promise<{ removed: boolean }>;
  isReady(): Promise<{ initialized: boolean }>;
  addListener(
    eventName: 'paymateStatus',
    listenerFunc: (event: PaymateStatusEvent) => void
  ): Promise<PluginListenerHandle>;
  addListener(
    eventName: 'paymateError',
    listenerFunc: (event: PaymateErrorEvent) => void
  ): Promise<PluginListenerHandle>;
  addListener(
    eventName: 'paymateReturn',
    listenerFunc: (event: PaymateReturnEvent) => void
  ): Promise<PluginListenerHandle>;
};

const PayMate = registerPlugin<PaymatePlugin>('PayMate');

export const isPaymateSupported = (): boolean => isCapacitorApp();

export const paymate = PayMate;
